import { useEffect, useState } from 'react';
import { NudosButton, NudosMaskedNumberInput } from 'components/NudosComponents';
import { Controller, ControllerRenderProps, useFormContext, useWatch } from 'react-hook-form';
import { ILogisticServiceEditionForm } from '../../LogisticServiceEdition.types';
import useSWR from 'swr';
import { getLogisticServiceTypes } from 'services/priceEdition/logisticServicesPriceEdition.service';
import BordModalConfirm from 'components/NudosComponents/BordModalConfirm/BordModalConfirm';
import { SWR_KEYS } from 'constants/constants';
import { CURRENCIES } from 'types/bussines';
import BordSelectDropdown from 'components/NudosComponents/BordSelectDropdown/BordSelectDropdown';

import './EditableServiceRow.scss';

interface IEditableServiceRowProps {
  removeAction: () => void;
  registerReference: `products.${number}.logisticServiceProductPrices.${number}`;
  productName: string;
  productSerial: string;
}

enum LOCAL_PRICE_INPUT_MODE {
  HIDDEN = 0,
  READONLY = 1,
  EDITABLE = 2
}

const EditableServiceRow = ({
  removeAction,
  registerReference,
  productName,
  productSerial
}: IEditableServiceRowProps) => {
  const { control, getValues, setValue } = useFormContext<ILogisticServiceEditionForm>();
  const exchangeRate = useWatch({ control, name: 'exchangeRate' });
  const service = useWatch({ control, name: registerReference });
  const { data: LSTypes } = useSWR(SWR_KEYS.listLSTypes, async () => {
    const types = await getLogisticServiceTypes();
    return types;
  });

  const [localPriceInputMode, setLocalPriceInputMode] = useState<LOCAL_PRICE_INPUT_MODE>(
    LOCAL_PRICE_INPUT_MODE.HIDDEN
  );
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const showLocalPriceInput =
    localPriceInputMode === LOCAL_PRICE_INPUT_MODE.EDITABLE ||
    localPriceInputMode === LOCAL_PRICE_INPUT_MODE.READONLY;

  const getLocalPriceInputMode = () => {
    const currency = getValues('currency');
    switch (currency) {
      case CURRENCIES.USD: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.HIDDEN);
        break;
      }
      case CURRENCIES.ARS: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.EDITABLE);
        break;
      }
      case CURRENCIES.BRL: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.EDITABLE);
        break;
      }
      default: {
        setLocalPriceInputMode(LOCAL_PRICE_INPUT_MODE.READONLY);
        break;
      }
    }
  };

  const handleBlurLocalPrice = (
    text: string = '',
    field: ControllerRenderProps<
      ILogisticServiceEditionForm,
      `products.${number}.logisticServiceProductPrices.${number}`
    >
  ) => {
    let newLocalPrice = Number(text) || 0;
    if (localPriceInputMode !== LOCAL_PRICE_INPUT_MODE.EDITABLE)
      return field.onChange({ ...field.value, totalValueLocal: newLocalPrice });
    const newExchangeRate = Math.ceil(newLocalPrice / Number(field.value.totalValueUsd) * 100) / 100;
    newLocalPrice = (Number(field.value.totalValueUsd) * newExchangeRate);
    field.onChange({ ...field.value, totalValueLocal: newLocalPrice, exchangeRate: newExchangeRate });
  };

  const handleRemoveAction = () => {
    setIsRemoveModalOpen(true);
  };

  const handleAcceptRemoveAction = async () => {
    removeAction();
    setIsRemoveModalOpen(false);
  };

  const handleObservableServiceFields = () => {
    const { totalValueUsd } = service || {};
    const localPrice = Number(totalValueUsd) * exchangeRate;
    if (totalValueUsd !== null && exchangeRate && localPriceInputMode !== LOCAL_PRICE_INPUT_MODE.HIDDEN) {
      setValue(registerReference, {
        ...service,
        totalValueLocal: localPrice,
        exchangeRate: exchangeRate
      });
    }
  };

  const renderDropdownOptions = (
    field: ControllerRenderProps<
      ILogisticServiceEditionForm,
      `products.${number}.logisticServiceProductPrices.${number}`
    >
  ) =>
    LSTypes?.map(type => (
      <button
        type="button"
        key={type.id}
        onClick={() =>
          field.onChange({
            ...field.value,
            logisticServiceTypeId: type.id,
            logisticServiceType: { id: type.id, name: type.name }
          })
        }
        className={`dropdownOption ${!!field.value.logisticServiceProductId ? 'disabled' : ''}`}
        disabled={!!field.value.logisticServiceProductId}
      >
        {type.name}
      </button>
    )) || [];

  // NOTA: totalValueUsd cambia cuando totalValueUsd o exchangeRate cambian. Si se requiere tener en cuenta más parámetros, solo actualizar este useEffect
  useEffect(() => {
    handleObservableServiceFields();
  }, [service.totalValueUsd, exchangeRate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getLocalPriceInputMode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Controller
      name={`${registerReference}`}
      control={control}
      render={({ field }) => (
        <section className="editableServiceRow">
          {isRemoveModalOpen && (
            <BordModalConfirm
              onCancel={() => setIsRemoveModalOpen(false)}
              onAccept={() => handleAcceptRemoveAction()}
              isLoading={false}
              isDisabled={false}
              title={
                <p>
                  Estas por eliminar el ítem <strong>{field.value.logisticServiceType?.name}</strong> de la herramienta{' '}
                  {productName} - serial: {productSerial} <br /> ¿seguro que deseas continuar?
                </p>
              }
              textButtonAccept="Sí, eliminar item"
              textButtonCancel="No, cancelar"
            />
          )}
          <BordSelectDropdown
            key={`EditableServiceRow-${registerReference}-1`}
            selectOptions={renderDropdownOptions(field)}
            currentValueOrplaceholder={field.value.logisticServiceType?.name || 'Servicio logístico'}
            isFilled={!!field.value.logisticServiceType?.name}
            label={'Descripción o concepto'}
          />
          <NudosMaskedNumberInput
            key={`EditableServiceRow-${registerReference}-2`}
            isFilled={true}
            label={'Precio con IVA en USD'}
            placeholder="$0.00 USD"
            handleChange={text => field.onChange({ ...field.value, priceUsd: text, totalValueUsd: text })}
            numberOfDecimals={2}
            maskPrefix="$"
            maskSufix=" USD"
            currentValue={String(field.value.totalValueUsd || '')}
          />
          {showLocalPriceInput && (
            <NudosMaskedNumberInput
              key={`EditableServiceRow-${registerReference}-3`}
              isFilled={true}
              label={`Precio con IVA en ${getValues('currency')}`}
              placeholder={`$0.00 ${getValues('currency')}`}
              handleBlur={text => handleBlurLocalPrice(text, field)}
              numberOfDecimals={2}
              maskPrefix="$"
              maskSufix={` ${getValues('currency')}`}
              currentValue={String(field.value.totalValueLocal || '')}
              customClassName="localPriceInput"
              isDeactivated={localPriceInputMode === LOCAL_PRICE_INPUT_MODE.READONLY}
            />
          )}
          <NudosButton
            customClassName="removeButton"
            buttonText="Eliminar"
            handleClick={() => handleRemoveAction()}
            type="button"
          />
        </section>
      )}
    />
  );
};

export default EditableServiceRow;
